import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  DevelopmentFramework,
  CreateFrameworkDTO,
  UpdateFrameworkDTO,
} from "../types/framework";

const FRAMEWORKS_COLLECTION = "developmentFrameworks";

// Parse markdown framework file into structured data
const parseFrameworkContent = (
  content: string,
): Partial<DevelopmentFramework> => {
  const framework: Partial<DevelopmentFramework> = {
    techStacks: [],
    architecturePatterns: [],
    codeTemplates: [],
    workflowSteps: [],
    bestPractices: [],
  };

  // Extract tech stacks from markdown
  const techStackMatch = content.match(
    /### 3\. Technology Stack Templates([\s\S]*?)### 4\./,
  );
  if (techStackMatch) {
    const stacksText = techStackMatch[1];
    const stacks = stacksText.match(/#### (.*?)\n([\s\S]*?)(?=####|$)/g) || [];
    framework.techStacks = stacks.map((stack) => ({
      name: stack.match(/#### (.*?)\n/)?.[1] || "",
      technologies: {},
      packages: [],
    }));
  }

  // Extract architecture patterns
  const archMatch = content.match(
    /### 4\. Architectural Patterns([\s\S]*?)### 5\./,
  );
  if (archMatch) {
    const archText = archMatch[1];
    const patterns = archText.match(/#### (.*?)\n([\s\S]*?)(?=####|$)/g) || [];
    framework.architecturePatterns = patterns.map((pattern) => ({
      name: pattern.match(/#### (.*?)\n/)?.[1] || "",
      description: "",
      structure: pattern.match(/```\n([\s\S]*?)\n```/)?.[1] || "",
    }));
  }

  // Extract code templates
  const templateMatch = content.match(
    /### 5\. Code Generation Templates([\s\S]*?)### 6\./,
  );
  if (templateMatch) {
    const templatesText = templateMatch[1];
    const templates =
      templatesText.match(/#### (.*?) Template\n([\s\S]*?)(?=####|$)/g) || [];
    framework.codeTemplates = templates.map((template) => ({
      name: template.match(/#### (.*?) Template/)?.[1] || "",
      category: "model" as const,
      code: template.match(/```typescript\n([\s\S]*?)\n```/)?.[1] || "",
      language: "typescript",
    }));
  }

  // Extract workflow steps
  const workflowMatch = content.match(
    /### 6\. Development Workflow([\s\S]*?)### 7\./,
  );
  if (workflowMatch) {
    const workflowText = workflowMatch[1];
    const phases =
      workflowText.match(/\d\. \*\*(.*?)\*\*([\s\S]*?)(?=\d\.|$)/g) || [];
    framework.workflowSteps = phases.map((phase) => ({
      phase: phase.match(/\*\*(.*?)\*\*/)?.[1] || "",
      tasks: (phase.match(/- \[ \] (.*?)(?:\n|$)/g) || []).map((t) =>
        t.replace("- [ ] ", "").trim(),
      ),
    }));
  }

  // Extract best practices
  const bestPracticesMatch = content.match(
    /### 7\. Best Practices Checklist([\s\S]*?)### 8\./,
  );
  if (bestPracticesMatch) {
    const bestPracticesText = bestPracticesMatch[1];
    const categories =
      bestPracticesText.match(/## (.*?)\n([\s\S]*?)(?=##|$)/g) || [];
    framework.bestPractices = categories.map((cat) => ({
      category: cat.match(/## (.*?)\n/)?.[1] || "",
      items: (cat.match(/- \[ \] (.*?)(?:\n|$)/g) || []).map((i) =>
        i.replace("- [ ] ", "").trim(),
      ),
    }));
  }

  return framework;
};

export class FrameworkService {
  // Create a new framework
  static async createFramework(
    userId: string,
    data: CreateFrameworkDTO,
    content: string,
  ): Promise<DevelopmentFramework> {
    try {
      const frameworkId = doc(collection(db, FRAMEWORKS_COLLECTION)).id;
      const parsedData = parseFrameworkContent(content);

      const framework: DevelopmentFramework = {
        frameworkId,
        userId,
        version: "1.0.0",
        title: data.title,
        description: data.description,
        content,
        techStacks: parsedData.techStacks || [],
        architecturePatterns: parsedData.architecturePatterns || [],
        codeTemplates: parsedData.codeTemplates || [],
        workflowSteps: parsedData.workflowSteps || [],
        bestPractices: parsedData.bestPractices || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isPublic: data.isPublic,
        forks: 0,
        tags: data.tags,
      };

      await setDoc(doc(db, FRAMEWORKS_COLLECTION, frameworkId), framework);
      return framework;
    } catch (error) {
      console.error("Error creating framework:", error);
      throw error;
    }
  }

  // Get framework by ID
  static async getFrameworkById(
    frameworkId: string,
  ): Promise<DevelopmentFramework | null> {
    try {
      const docSnap = await getDoc(doc(db, FRAMEWORKS_COLLECTION, frameworkId));
      return docSnap.exists() ? (docSnap.data() as DevelopmentFramework) : null;
    } catch (error) {
      console.error("Error getting framework:", error);
      throw error;
    }
  }

  // Get user's frameworks
  static async getUserFrameworks(
    userId: string,
  ): Promise<DevelopmentFramework[]> {
    try {
      const q = query(
        collection(db, FRAMEWORKS_COLLECTION),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        (doc) => doc.data() as DevelopmentFramework,
      );
    } catch (error) {
      console.error("Error getting user frameworks:", error);
      throw error;
    }
  }

  // Get public frameworks
  static async getPublicFrameworks(
    limit = 20,
  ): Promise<DevelopmentFramework[]> {
    try {
      const q = query(
        collection(db, FRAMEWORKS_COLLECTION),
        where("isPublic", "==", true),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .slice(0, limit)
        .map((doc) => doc.data() as DevelopmentFramework);
    } catch (error) {
      console.error("Error getting public frameworks:", error);
      throw error;
    }
  }

  // Update framework
  static async updateFramework(
    frameworkId: string,
    data: UpdateFrameworkDTO,
  ): Promise<void> {
    try {
      const updateData: any = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      if (data.content) {
        const parsedData = parseFrameworkContent(data.content);
        Object.assign(updateData, parsedData);
      }

      await updateDoc(doc(db, FRAMEWORKS_COLLECTION, frameworkId), updateData);
    } catch (error) {
      console.error("Error updating framework:", error);
      throw error;
    }
  }

  // Fork framework
  static async forkFramework(
    userId: string,
    sourceFrameworkId: string,
  ): Promise<DevelopmentFramework> {
    try {
      const sourceFramework = await this.getFrameworkById(sourceFrameworkId);
      if (!sourceFramework) {
        throw new Error("Source framework not found");
      }

      const newFrameworkId = doc(collection(db, FRAMEWORKS_COLLECTION)).id;
      const forkedFramework: DevelopmentFramework = {
        ...sourceFramework,
        frameworkId: newFrameworkId,
        userId,
        version: sourceFramework.version,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isPublic: false,
        forks: 0,
      };

      await setDoc(
        doc(db, FRAMEWORKS_COLLECTION, newFrameworkId),
        forkedFramework,
      );

      // Increment forks on source framework
      await updateDoc(doc(db, FRAMEWORKS_COLLECTION, sourceFrameworkId), {
        forks: sourceFramework.forks + 1,
      });

      return forkedFramework;
    } catch (error) {
      console.error("Error forking framework:", error);
      throw error;
    }
  }

  // Delete framework
  static async deleteFramework(frameworkId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, FRAMEWORKS_COLLECTION, frameworkId));
    } catch (error) {
      console.error("Error deleting framework:", error);
      throw error;
    }
  }

  // Export framework as markdown
  static exportAsMarkdown(framework: DevelopmentFramework): string {
    return framework.content;
  }

  // Export framework as JSON
  static exportAsJSON(framework: DevelopmentFramework): string {
    return JSON.stringify(framework, null, 2);
  }

  // Generate project initialization script
  static generateInitScript(
    framework: DevelopmentFramework,
    techStackName: string,
  ): string {
    const techStack = framework.techStacks.find(
      (ts) => ts.name === techStackName,
    );
    if (!techStack) {
      return "# Tech stack not found";
    }

    return `
# Initialize Project with ${techStackName}

## Install Dependencies
\`\`\`bash
npm install
${techStack.packages.map((p) => `npm install ${p}`).join("\n")}
\`\`\`

## Configure Project
1. Copy .env.example to .env
2. Update environment variables
3. Configure your backend
4. Set up database

## Start Development
\`\`\`bash
npm run dev
\`\`\`

## Testing
\`\`\`bash
npm run test
npm run test:coverage
\`\`\`

## Build for Production
\`\`\`bash
npm run build
npm run start
\`\`\`

See the framework documentation for more details.
    `.trim();
  }
}
